import { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { useI18n } from '../../context/useI18n';
import { translateStatus } from '../../utils/i18nHelpers';
import { Trophy, Ruler } from 'lucide-react';
import { useAuth } from '../../context/useAuth';
import D28dChallengeWizard from './D28dChallengeWizard';
import './d28d-consumer-challenges.css';

export default function D28dChallengesPanel({ programId = null, compact = false }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [active, setActive] = useState(null);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [enrolledIds, setEnrolledIds] = useState(() => new Set());
  const [busyId, setBusyId] = useState(null);

  const loadChallenges = useCallback(async () => {
    const params = programId ? { program_id: programId } : {};
    try {
      const r = await api.get('/d28d/challenges', { params });
      setChallenges((r.data?.data || []).filter((c) => c.estado !== 'draft' && c.estado !== 'cancelled'));
      setError('');
    } catch {
      setChallenges([]);
      setError(t('d28d.challenges.load_error', 'No se pudieron cargar las premiaciones del ciclo.'));
    }
  }, [programId, t]);

  useEffect(() => {
    loadChallenges();
  }, [loadChallenges]);

  useEffect(() => {
    const enrolled = challenges.filter((c) => c.user_enrolled).map((c) => c.id);
    if (!enrolled.length) return;
    setEnrolledIds((prev) => new Set([...prev, ...enrolled]));
  }, [challenges]);

  const enroll = async (id) => {
    setBusyId(id);
    setError('');
    try {
      await api.post(`/d28d/challenges/${id}/enroll`);
      setEnrolledIds((prev) => new Set(prev).add(id));
      setMsg(t('d28d.challenges.enrolled_ok', 'Inscripción confirmada'));
      await loadChallenges();
    } catch (err) {
      setError(err.response?.data?.error || t('d28d.challenges.enroll_error', 'No se pudo completar la inscripción.'));
    } finally {
      setBusyId(null);
    }
  };

  const withdraw = async (id) => {
    setBusyId(id);
    setError('');
    try {
      await api.post(`/d28d/challenges/${id}/withdraw`);
      setEnrolledIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      setMsg(t('d28d.challenges.withdrawn', 'Te retiraste del evento'));
      await loadChallenges();
    } catch (err) {
      setError(err.response?.data?.error || t('d28d.challenges.withdraw_error', 'No se pudo retirar la inscripción.'));
    } finally {
      setBusyId(null);
    }
  };

  const isEnrolled = (c) => enrolledIds.has(c.id) || c.user_enrolled === true;
  const myWins = challenges.filter(
    (c) => c.estado === 'published'
      && (c.user_podium_place != null || (c.podium || []).some((p) => Number(p.user_id) === Number(user?.id))),
  );

  return (
    <div className="d28d-challenge-panel space-y-4">
      {myWins.map((c) => {
        const place = c.user_podium_place
          ?? (c.podium || []).find((p) => Number(p.user_id) === Number(user?.id))?.lugar;
        return (
          <div key={`win-${c.id}`} className="card d28d-challenge-panel__win p-4 sm:p-5">
            <p className="d28d-challenge-panel__win-label text-xs font-bold uppercase m-0">
              {t('d28d.challenges.you_won', '¡Ganaste la premiación!')}
            </p>
            <p className="font-bold text-lg text-[var(--d28d-text)] mt-1.5 m-0">{c.nombre}</p>
            <p className="text-sm d28d-text-muted mt-1 m-0">
              {t('d28d.challenges.you_won_place', 'Obtuviste el {place}° lugar', { place })}
              {c.premio ? ` · ${c.premio}` : ''}
            </p>
          </div>
        );
      })}

      {!compact ? (
        <h3 className="text-lg font-bold text-[var(--d28d-text)] flex items-center gap-2 m-0">
          <Trophy className="w-5 h-5 text-[var(--shell-primary,var(--d28d-yellow,#ffd700))]" aria-hidden />
          {t('d28d.challenges.title', 'Premiaciones por ciclo')}
        </h3>
      ) : null}

      {msg ? <p className="d28d-challenge-panel__msg text-sm m-0">{msg}</p> : null}
      {error ? <p className="text-sm text-red-400 m-0">{error}</p> : null}

      {!challenges.length ? (
        <p className="text-sm d28d-text-muted m-0">
          {t('d28d.challenges.empty', 'No hay premiaciones activas en este ciclo.')}
        </p>
      ) : null}

      {challenges.map((c) => (
        <div key={c.id} className="card p-4 sm:p-5 space-y-2">
          <p className="font-semibold text-[var(--d28d-text)] text-base m-0">{c.nombre}</p>
          {c.descripcion ? (
            <p className="text-sm d28d-text-muted m-0 leading-relaxed">{c.descripcion}</p>
          ) : null}
          {c.objetivo ? (
            <p className="text-sm text-[var(--d28d-text)] m-0 flex items-start gap-1.5">
              <Ruler className="w-4 h-4 shrink-0 mt-0.5 opacity-80" aria-hidden />
              <span>{c.objetivo}</span>
            </p>
          ) : null}
          {isEnrolled(c) ? (
            <span className="d28d-challenge-panel__badge">
              {t('d28d.challenges.enrolled_badge', 'Inscrito en este ciclo')}
            </span>
          ) : null}
          {c.premio ? (
            <p className="text-sm d28d-text-accent m-0">
              {t('d28d.challenges.prize', 'Premio')}: {c.premio}
            </p>
          ) : null}
          <p className="text-xs d28d-text-muted m-0">
            {t('d28d.challenges.status', 'Estado')}: {translateStatus(c.estado, t)}
          </p>

          {c.estado === 'active' ? (
            <div className="flex gap-2 mt-2 flex-wrap">
              {!isEnrolled(c) ? (
                <button type="button" className="btn-primary text-sm" disabled={busyId === c.id} onClick={() => enroll(c.id)}>
                  {t('d28d.challenges.join', 'Inscribirme en la prueba')}
                </button>
              ) : (
                <button type="button" className="btn-secondary text-sm" disabled={busyId === c.id} onClick={() => withdraw(c.id)}>
                  {t('d28d.challenges.withdraw', 'Retirarme')}
                </button>
              )}
              {isEnrolled(c) ? (
                <button
                  type="button"
                  className="btn-secondary text-sm"
                  onClick={() => setActive(active === c.id ? null : c.id)}
                >
                  {t('d28d.challenges.upload_evidence', 'Subir resultados y fotos')}
                </button>
              ) : null}
            </div>
          ) : null}

          {active === c.id && isEnrolled(c) ? (
            <D28dChallengeWizard challengeId={c.id} onComplete={loadChallenges} />
          ) : null}

          {c.user_eligibility && !c.user_eligibility.attendance_ok && isEnrolled(c) ? (
            <p className="d28d-challenge-panel__warn text-xs m-0 mt-2">
              {t('d28d.challenges.need_classes', 'Necesitas {n} clases — llevas {a}', {
                n: c.user_eligibility.min_classes,
                a: c.user_eligibility.classes_attended,
              })}
            </p>
          ) : null}

          {c.estado === 'published' && c.podium?.length > 0 ? (
            <div className="d28d-challenge-panel__podium">
              <p className="d28d-challenge-panel__podium-title">
                {t('d28d.challenges.winners', 'Ganadores del ciclo')}
              </p>
              {c.podium.map((p) => (
                <p key={p.lugar} className="d28d-challenge-panel__podium-row">
                  {p.lugar}° — {p.user_name}
                  {p.puntuacion != null ? ` (${p.puntuacion} pts)` : ''}
                </p>
              ))}
            </div>
          ) : null}

          {c.ranking?.length > 0 ? (
            <div className="mt-1 text-xs d28d-text-muted">
              {t('d28d.challenges.ranking', 'Ranking')}:{' '}
              {c.ranking.slice(0, 5).map((r) => `#${r.position} ${r.user_name}`).join(' · ')}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}
